/**
 * wf-api — typed wrappers for the Workforce data layer, through the
 * @v0y4ge/api-client `apiRaw` choke point.
 *
 * The workforce operations are DECLARED (spec: specs/workforce-management-spec-2026-07-16.md
 * + the 2026-07-16 changelog) and pending in @v0y4ge/api-contracts / api-mocks —
 * Claude Code owns that layer. Until the ops land, calls reject and the screens
 * render an honest "data layer pending" state. When named ops ship in
 * @v0y4ge/api-client these wrappers swap to them with no screen changes.
 *
 * Types mirror the declared contract shapes byte-for-byte (see spec §4).
 */
import { apiRaw } from "@v0y4ge/api-client";
import { reportOpSource } from "../../shell/opSource";

/** Data-source transparency (2026-07-16 convention): every op result is
 * inspected for the `__source: "cd-preview-seed"` marker and reported, so
 * screens can flag non-integrated calls via <DataSourceStrip>. Clean backend
 * responses never carry the marker. */
const tag = <T,>(op: string, p: Promise<T>): Promise<T> =>
  p.then((r) => { reportOpSource(op, r); return r; });

export type DemandSource = "events" | "appointments" | "queue";

export interface WfShift {
  id: string;
  staffId: string | null;
  staffName: string | null;
  role: string;
  locationId: string;
  date: string;
  start: string;
  end: string;
  breaks: { start: string; end: string; kind: "break" | "lunch" }[];
  source: "manual" | "autofill" | "template" | "event";
  eventId?: string | null;
  status: "draft" | "published" | "open";
  ruleFlags: string[];
  /** Set when the row is a day off rather than a worked shift (REQ-096f). */
  dayOffLabel?: string | null;
}

export interface WfStaffLite {
  id: string; name: string; initials: string; color?: string;
  role?: string; skills: string[]; hoursScheduled: number; hoursTarget: number;
}

export interface DemandSlice { t: string; events: number; appointments: number; queue: number; }
export interface DemandDriver {
  id: string; kind: DemandSource; label: string; date: string;
  start: string; end: string; heads: number; refId?: string;
}
export interface DayDemand { date: string; slices: DemandSlice[]; drivers: DemandDriver[]; }

export interface ScheduleWeek {
  weekStart: string; tz: string; locationId: string; locationName: string;
  // slotMinutes: grid granularity. Optional until the server ships it
  // (Schedule port stage 1) — the screen falls back to 15-minute slots.
  window: { startHour: number; endHour: number; slotMinutes?: 15 | 30 };
  status: "draft" | "published" | "partial"; version: number; publishedAt?: string | null;
  staff: WfStaffLite[]; shifts: WfShift[]; demand: DayDemand[];
}

export interface ForecastResponse {
  weekStart: string; tz: string; locationId: string; locationName: string;
  days: DayDemand[];
  staffedHours: { date: string; scheduled: number; required: number }[];
  accuracy?: { last4wMape?: number } | null;
}

export interface FillSuggestion {
  id: string; kind: "assign" | "extend" | "open-shift" | "swap-in";
  staffId?: string; staffName?: string; detail: string;
}
export interface CoverageGap {
  id: string; date: string; start: string; end: string; locationId: string;
  skill?: string | null; needed: number; scheduled: number; severity: "critical" | "low";
  source: DemandSource; driverLabel?: string; suggestions: FillSuggestion[];
}
export interface CoverageResponse { weekStart: string; tz: string; gaps: CoverageGap[]; }

export interface EventStaffingRole {
  id: string; label: string; count: number; skill?: string | null;
  window: { start: string; end: string };
  assignees: { shiftId: string; staffId: string; staffName: string }[];
}
export interface EventStaffing {
  eventId: string; title: string; date: string; start: string; end: string;
  tz: string; locationId: string; locationName: string;
  capacity: number | null; registered: number; eventStatus: string;
  readiness: "ready" | "partial" | "unstaffed"; suggested: boolean;
  roles: EventStaffingRole[];
}

export interface SwapRequest {
  id: string; kind: "swap" | "claim"; status: "pending" | "approved" | "declined";
  from: { staffId: string; name: string }; to?: { staffId: string; name: string } | null;
  shiftId: string; date: string; start: string; end: string; role: string; locationId: string;
  reason?: string | null; createdAt: string; ruleFlags: string[];
}
export interface TimeOffRequest {
  id: string; kind: "timeoff"; status: "pending" | "approved" | "declined";
  staffId: string; staffName: string; type: "vacation" | "sick" | "personal" | "other";
  startDate: string; endDate: string; allDay: boolean; note?: string | null;
  createdAt: string; balanceDaysLeft?: number | null; ruleFlags: string[];
}
export type WfRequest = SwapRequest | TimeOffRequest;

export interface LaborRule {
  id: string; label: string; description: string;
  kind: "rest" | "maxhours" | "break" | "minor" | "overtime" | "custom";
  params: Record<string, number | string | boolean>; enabled: boolean;
  jurisdiction?: string | null; updatedAt: string;
}
export interface RuleViolation {
  id: string; ruleId: string; ruleLabel: string; severity: "block" | "warn";
  staffId: string; staffName: string; date: string; shiftId?: string | null;
  detail: string; resolved: boolean;
}

export interface OpsQueue {
  id: string; name: string; waiting: number; avgWaitMins: number; longestWaitMins: number;
  staffed: number; needed: number;
  entries: { id: string; name: string; joinedAt: string; service?: string | null; status: "waiting" | "called" | "serving" }[];
}
export interface OpsAppointment {
  id: string; start: string; end: string; service: string; staffName: string;
  customer: string; status: "upcoming" | "checked-in" | "in-progress" | "done" | "no-show";
}
export interface OpsEvent {
  id: string; title: string; start: string; end: string; checkedIn: number;
  registered: number; staffOnShift: number; staffNeeded: number; status: string;
}
export interface OpsFloorEntry {
  staffId: string; name: string; role: string;
  status: "on-shift" | "break" | "off-soon" | "late"; until: string;
}
export interface OpsBoardSnapshot {
  asOf: string; tz: string; locationId: string; locationName: string;
  queues: OpsQueue[]; appointments: OpsAppointment[]; events: OpsEvent[]; floor: OpsFloorEntry[];
}

export interface MyScheduleResponse {
  staffId: string; weekStart: string; tz: string; locationName: string;
  hours: { scheduled: number; target: number };
  shifts: WfShift[]; openShifts: WfShift[]; requests: WfRequest[];
}

/* ── Aperture Bank wave 1 · Personal Banker surfaces (declared 2026-07-17,
 * REQ-036; scope: self — the signed-in staff member — + date/range params;
 * wall-clock convention per spec §4) ── */
export interface MyDayAppointment {
  id: string; start: string; end: string; service: string; serviceShort?: string | null;
  /* REQ-128 (2026-08-03): the appointment carries its CATALOGUE identity and the pre/post
     minutes that come with it, so no surface has to re-join by service name. */
  serviceId?: string | null; preMin?: number; postMin?: number; blockMin?: number | null;
  sub?: string | null; customer: string; repeat: boolean;
  method: "in-person" | "phone" | "video";
  status: "upcoming" | "checked-in" | "in-progress" | "completed" | "no-show";
  outcome?: "sale-closed" | "in-progress" | "follow-up" | "no-progress" | "escalation" | null;
  opportunity?: number | null; noShowRisk?: "low" | "medium" | "high" | null;
  customer360?: {
    since: string; tenure: string; tier: string; lifetime: number; lifetimeUnit: string;
    churnRisk: "low" | "med" | "high"; riskScore: number; products: string[];
    opportunities: { type: string; why: string; action: string; score: number }[];
    lastInteractions: { when: string; advisor: string; note: string; channel: "in-person" | "phone" | "video" | "chat" }[];
    intake: { q: string; a: string }[];
    tasks: { label: string; done: boolean }[];
  } | null;
  talkingPoints?: string[] | null;
  /** Full prior capture, echoed back once saved (reopen-for-edit prefill). */
  capturedNote?: string | null; capturedOpportunityType?: string | null;
  capturedRepeat?: boolean | null; capturedCoachingFlag?: boolean | null; capturedCsat?: number | null;
}
export interface MyDayWalkIn { id: string; name: string; reason: string; waitedMins: number; opportunity?: number | null; member: boolean; estMins: number; }
export interface MyDayResponse {
  date: string; tz: string; locationName: string;
  scheduleUpdate?: { weekLabel: string; shiftCount: number; changedCount: number; acknowledgeBy: string; acknowledged: boolean; shifts: { date: string; start: string; end: string; isNew: boolean }[] } | null;
  shift: { id: string; start: string; end: string; breaks: { start: string; end: string; kind: "break" | "lunch" }[] } | null;
  availability: "available" | "busy" | "away";
  counts: { booked: number; completed: number; walkIns: number };
  appointments: MyDayAppointment[];
  walkIns: MyDayWalkIn[];
}
export type MyPerfRange = "7d" | "14d" | "30d" | "60d" | "90d" | "qtd" | "half" | "ytd" | "lastQuarter" | "lastYear" | "custom";
export interface MyPerfServiceRow {
  service: string; count: number; avgDurationMins: number; conversionPct: number;
  minutes?: number; csat?: number | null;
  branchAvgDurationMins?: number | null; branchConversionPct?: number | null; branchCsat?: number | null;
  sharePct?: number; branchSharePct?: number | null;
  noShows?: number; cancellations?: number;
}
export interface MyPerfTrendPoint {
  date: string; conversionPct: number; avgDurationMins: number;
  period?: "week" | "month"; partial?: boolean; appts?: number; surveyScore?: number | null;
  branchAvgDurationMins?: number | null; branchConversionPct?: number | null; branchSurveyScore?: number | null;
}
export interface MyPerfGoal {
  metric: string; target: number; direction: "lower" | "higher";
  setBy: string; setByRole: string; scope: "branch" | "region" | "network";
  effectiveFrom: string; note?: string | null;
  current: number | null; attainmentPct: number | null; met: boolean | null;
}
export interface MyCoachingEntry {
  id: string; date: string; title: string; body: string; by: string;
  theme?: string | null; byRole?: string | null;
  kind?: "1on1" | "training" | "note" | "recognition";
  metric?: string | null; baseline?: number | null; latest?: number | null;
  direction?: "improving" | "flat" | "slipping" | null;
  assessment?: number | null;
  training?: { provider: string; minutes: number; courseCode?: string | null } | null;
  dueDate?: string | null; completedAt?: string | null;
  acknowledgedAt?: string | null; reflection?: string | null;
}
export interface MyPerfAppointment {
  id: string; date: string; time: string; service: string; customer: string; durationMin: number;
  status: "completed" | "no-show" | "cancelled";
  outcome?: string | null; csat?: number | null; opportunity?: string | null; coachingFlag?: boolean;
}
export interface CatalogueService {
  id: string; name: string; lob: string; intent: string; category: string;
  description?: string | null;
  durationMin: number | null; preMin: number; postMin: number; blockMin: number;
  channels: { inPerson: boolean; phone: boolean; video: boolean };
  /** WHERE it can be booked, alongside `channels` (HOW it is delivered). */
  modules: { appointments: boolean; queueing: boolean; events: boolean };
  /** ONE booking-question group per method (operator ruling, 2026-08-03). */
  questionGroups: { appointments: string | null; queueing: string | null; events: string | null };
  disableBefore?: string | null; disableAfter?: string | null;
  bookableNow?: boolean;
  unavailableReason?: "archived" | "before-window" | "after-window" | null;
  minAdvance: string | null; maxAdvance: string | null; minCancel: string | null;
  /** Who may INITIATE a booking. "customer" means customers and advisors both (an advisor can
   *  always book what a customer can); "advisor" means advisor-only. Replaced the
   *  `onlineBookable` boolean 2026-08-03 — that duplicated the module pills and nothing read it. */
  bookedBy: "customer" | "advisor";
  /** Optional artwork shown on customer-facing journeys (appointments, queueing, events). */
  imageUrl?: string | null; imageAlt?: string | null;
  archived: boolean;
}
export interface CatalogueQuestionGroup { id: string; name: string; questions: number; note?: string | null }
export interface CatalogueLob { id: string; name: string; activated: boolean; description?: string | null }
export interface CatalogueIntent { id: string; name: string; lob: string }
export interface CatalogueAdvisor { name: string; role: string; lobs: string[]; source: string; services?: string[]; servicesSource?: string; branch?: string | null }
export interface CatalogueImportRow { line: number; name: string; ok: boolean; reason?: string; serviceId?: string }
export interface CatalogueImportRun {
  id: string; at: string; by: string; byRole: string | null;
  uploadedAt?: string; completedAt?: string;
  status?: "completed" | "partial" | "failed";
  fileName: string; fileSize: number | null;
  rowCount: number; imported: number; failed: number;
  results: CatalogueImportRow[];
  /** The original file, kept verbatim so the history can hand it back. */
  fileText: string | null;
}
export interface ServiceCatalogue {
  lobs: CatalogueLob[]; intents: CatalogueIntent[]; categories: CatalogueIntent[];
  services: CatalogueService[];
  questionGroups: CatalogueQuestionGroup[];
  imports?: CatalogueImportRun[];
  advisors: CatalogueAdvisor[]; roleLobs: Record<string, string[]>;
  branches: string[]; branchLobs: Record<string, string[]>;
  scopeBranch: string | null; intentLabel: string;
}
export interface MyServicesView {
  staffName: string; role: string | null; locationName: string | null;
  myLobs: { id: string; name: string }[];
  assignedBy: "role" | "operations";
  services: CatalogueService[]; otherLobServices: number;
  /** Everything this BRANCH offers, whatever line it belongs to (read-only audiences). */
  branchServices?: CatalogueService[];
  questionGroups?: CatalogueQuestionGroup[];
  /** Catalogue ids this advisor is currently capable of (what My Skills ticks). */
  myServices?: string[];
  intents: CatalogueIntent[]; lobs: CatalogueLob[]; readOnly: boolean;
}

export interface BookableEvent {
  id: string; title: string; category: string | null; description: string | null;
  startsAt: string; endsAt: string; timezone: string | null;
  locationName: string | null; hostName: string | null;
  capacity: number | null; registeredCount: number; waitlistCount: number; seatsLeft: number | null;
  full: boolean; waitlistEnabled: boolean; overrideAllowed: boolean;
  format: string | null; tags: string[]; status: string;
}
export interface MyEventsResponse {
  events: BookableEvent[]; total: number; nextCursor: number | null;
  facets: { branches: string[]; categories: string[] };
  scope: "network" | "branch";
}
export interface StaffReferral {
  id: string; kind: "event" | "appointment"; customer: string;
  sentVia: "registered" | "link" | "booked" | "handoff"; sentAt: string;
  outcome: "sent" | "registered" | "waitlisted" | "attended" | "booked" | "completed" | "declined" | "no-show" | "expired";
  outcomeAt?: string | null; note?: string | null;
  eventId?: string | null; eventTitle?: string | null; locationName?: string | null; seatKind?: string | null;
  lob?: string | null; service?: string | null; toStaff?: string | null;
}
export interface MyReferralsResponse {
  staffName: string | null; referrals: StaffReferral[];
  counts: { total: number; event: number; appointment: number; landed: number; waiting: number };
}
export interface EventRegistrationResult {
  registration: { id: string; eventId: string; customerName: string; mode: "seat" | "waitlist" | "override" };
  event: BookableEvent; referral: StaffReferral | null;
}
export interface EventRegistrationLink { url: string; expiresAt: string | null; referral: StaffReferral | null }

export interface MyPerformanceResponse {
  staffId: string; range: string; tz: string;
  metrics: {
    avgDurationMins: number; branchAvgDurationMins: number;
    conversionPct: number; branchConversionPct: number;
    customerScore: number; branchCustomerScore: number;
    noShowPct: number; branchNoShowPct: number;
  };
  byService: MyPerfServiceRow[];
  trend: MyPerfTrendPoint[];
  coachingLog: MyCoachingEntry[];
  /* REQ-126 additive block (2026-08-03) — see api-contracts/src/workforce.ts. */
  staffName?: string; role?: string | null; locationName?: string | null;
  requestedFrom?: string | null; requestedTo?: string | null;
  metricsAvailable?: boolean; minSample?: number;
  totals?: { completed: number; noShows: number; cancellations: number; booked: number };
  activity?: {
    from: string; to: string; completed: number; noShows: number; cancellations: number; booked: number;
    minutes: number; hours: number; days: number; perDay: number; flagged: number; partialWindow: boolean;
    hasDetail?: boolean;
  };
  outcomes?: { id: string; label: string; count: number; pct: number }[];
  goals?: MyPerfGoal[];
  customerScoreModel?: {
    surveyWeightPct: number; coachingWeightPct: number;
    setBy: string | null; setByRole: string | null; approvedBy: string | null; approvedByRole: string | null;
    surveyScore: number | null; surveyResponses: number;
    coachingScore: number | null; coachingEntries: number;
    branchSurveyScore: number | null; branchCoachingScore: number | null; note: string | null;
  };
  appointments?: MyPerfAppointment[];
  coverage?: {
    from: string; to: string; days: number; granularity: "week" | "month";
    detailFrom: string; weeklyFrom: string; detailCoversWindow: boolean;
    perServiceBranchFrom: string; note: string | null;
  };
  peer?: { role: string; count: number; basis?: string | null } | null;
}
export interface MyTimeDayEntry {
  date: string; kind: "work" | "pto" | "pto-pending" | "closed"; label?: string | null;
  shift?: { id: string; start: string; end: string; breaks: { start: string; end: string; kind: "break" | "lunch" }[] } | null;
  appointments?: { id: string; start: string; end: string; service: string; customer: string; color: string }[];
}
export interface IncomingSwap { id: string; fromName: string; theirShift: { date: string; start: string; end: string }; yourShift: { date: string; start: string; end: string }; note?: string | null; }
export interface TimeOffEntry { id: string; type: "PTO" | "Sick" | "Personal"; startDate: string; endDate: string; submittedDate: string; reason: string; status: "pending" | "approved" | "declined"; }
export interface MyTimeResponse {
  staffId: string; tz: string;
  scheduleUpdate?: { weekLabel: string; shiftCount: number; changedCount: number; acknowledgeBy: string; acknowledged: boolean } | null;
  kpis: { pendingRequests: number; awaitingMyReply: number; openShiftsAvailable: number; ptoBalanceDays: number; premiumEarnedYtd: number };
  incomingSwaps: IncomingSwap[];
  timeOffEntries: TimeOffEntry[];
  balances: { vacationDaysLeft: number; sickDaysLeft: number; personalDaysLeft: number };
  myRequests: WfRequest[];
  myShifts: { id: string; date: string; start: string; end: string; apptCount: number }[];
  openShifts: (WfShift & { premiumPct?: number | null; postedBy?: string | null; reason?: string | null; claims?: number; matchesMySkills?: boolean })[];
  history: { ts: string; event: string; detail: string; who: string }[];
}

export interface DemoPersonaDto {
  id: string; displayName?: string; name?: string; email?: string; role?: string;
  blurb?: string; tenantId?: string;
  /** Bare BCP-47 tag of the persona's saved language preference (kit demo-personas fixture; omitted ⇒ en). */
  locale?: string;
  assignments?: Partial<Record<string, string>>;
  /** Tenant ids this user can switch between (drives account.workspaces + active-workspace). */
  workspaces?: string[];
}
export interface DemoTenant {
  id: string; name: string; industry: string; blurb: string;
  brandColor?: string; initials?: string; personas: DemoPersonaDto[];
}

const q = (params: Record<string, string | undefined>) => {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v) sp.set(k, v); });
  const s = sp.toString();
  return s ? `?${s}` : "";
};
const json = (body: unknown): RequestInit => ({ method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });

/* ── Demo login ─────────────────────────────────────────────────────── */
export const getDemoTenants = () =>
  tag("getDemoTenants", apiRaw<{ tenants: DemoTenant[] }>("/demo/tenants"));

/* ── Schedule ───────────────────────────────────────────────────────── */
export const getScheduleWeek = (weekStart: string, locationId?: string) =>
  tag("getScheduleWeek", apiRaw<ScheduleWeek>(`/v1/admin/workforce/schedule${q({ weekStart, locationId })}`, undefined, 'getScheduleWeek'));
export const createShift = (input: Partial<WfShift>) =>
  tag("createShift", apiRaw<WfShift>("/v1/admin/workforce/shifts", json(input), 'createShift'));
export const updateShift = (id: string, patch: Partial<WfShift>) =>
  tag("updateShift", apiRaw<WfShift>(`/v1/admin/workforce/shifts/${id}`, { ...json(patch), method: "PATCH" }, 'updateShift'));
export const deleteShift = (id: string) =>
  tag("deleteShift", apiRaw<{ ok: boolean }>(`/v1/admin/workforce/shifts/${id}`, { method: "DELETE" }, 'deleteShift'));
export const publishScheduleWeek = (weekStart: string, locationId?: string) =>
  tag("publishScheduleWeek", apiRaw<ScheduleWeek>("/v1/admin/workforce/schedule/publish", json({ weekStart, locationId }), 'publishScheduleWeek'));

/**
 * Server-side compliance evaluation for a shift being drafted (REQ-096b).
 * NOT YET SERVED — the wrapper is wired so the drawer fails loud and honest
 * rather than re-implementing jurisdiction arithmetic client-side.
 */
export interface ShiftRuleCheck {
  id: string; name: string; param: string;
  status: "ok" | "auto" | "warn" | "block"; msg: string;
}
export interface ShiftRuleEvaluation {
  checks: ShiftRuleCheck[];
  breaks: { start: string; end: string; kind: "break" | "lunch" }[];
}
export interface ShiftDraftInput {
  staffIds: string[]; locationId: string; dateFrom: string; dateTo: string;
  start: string; end: string; role: string;
}
/**
 * Unserved operations do not always fail: the preview layer can leave a request
 * pending forever, and a call-site race proved unreliable (a screen's local
 * state never observed it). Every op below that has no server implementation
 * yet is raced here, in the data layer, so "nothing answered" always becomes a
 * rejection the UI can report. Verified failure mode: previewPublishImpact
 * fires on the wire and never settles.
 */
const OP_TIMEOUT_MS = 6000;
function withOpTimeout<T>(label: string, p: Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`${label} did not answer within ${OP_TIMEOUT_MS / 1000}s`)), OP_TIMEOUT_MS);
    p.then((v) => { clearTimeout(timer); resolve(v); }, (e) => { clearTimeout(timer); reject(e instanceof Error ? e : new Error(String(e))); });
  });
}

export const evaluateShiftRules = (draft: ShiftDraftInput) =>
  tag("evaluateShiftRules", withOpTimeout("evaluateShiftRules", apiRaw<ShiftRuleEvaluation>("/v1/admin/workforce/shifts/evaluate", json(draft), 'evaluateShiftRules')));

/**
 * Schedule-week actions the port needs that no operation serves yet
 * (REQ-096d/e). Wired typed so the screen fails loud instead of faking them.
 */
export interface PublishImpact {
  draftChanges: number; budgetHours: number; projectedOtHours: number;
  noticeDays: number; noticeMinimumDays: number; warnings: string[]; rulesVersion: string;
}
export const previewPublishImpact = (weekStart: string, locationId?: string) =>
  tag("previewPublishImpact", withOpTimeout("previewPublishImpact", apiRaw<PublishImpact>(`/v1/admin/workforce/schedule/publish-impact${q({ weekStart, locationId })}`, undefined, 'previewPublishImpact')));
export const discardScheduleDraft = (weekStart: string, locationId?: string) =>
  tag("discardScheduleDraft", withOpTimeout("discardScheduleDraft", apiRaw<{ ok: boolean }>("/v1/admin/workforce/schedule/discard", json({ weekStart, locationId }), 'discardScheduleDraft')));
export const syncBookings = (locationId?: string) =>
  tag("syncBookings", withOpTimeout("syncBookings", apiRaw<{ status: string; added: number; updated: number; syncedAt: string }>("/v1/admin/workforce/schedule/sync-bookings", json({ locationId }), 'syncBookings')));
export const copyPreviousWeek = (weekStart: string, locationId?: string) =>
  tag("copyPreviousWeek", withOpTimeout("copyPreviousWeek", apiRaw<ScheduleWeek>("/v1/admin/workforce/schedule/copy-previous-week", json({ weekStart, locationId }), 'copyPreviousWeek')));
export const generateSchedule = (input: { weekStart: string; locationId?: string; objective?: string }) =>
  tag("generateSchedule", withOpTimeout("generateSchedule", apiRaw<ScheduleWeek>("/v1/admin/workforce/schedule/generate", json(input), 'generateSchedule')));

/* ── Demand & coverage ──────────────────────────────────────────────── */
export const getDemandForecast = (weekStart: string, locationId?: string) =>
  tag("getDemandForecast", apiRaw<ForecastResponse>(`/v1/admin/workforce/demand${q({ weekStart, locationId })}`, undefined, 'getDemandForecast'));
export const recalcDemandForecast = (weekStart: string, locationId?: string) =>
  tag("recalcDemandForecast", apiRaw<ForecastResponse>("/v1/admin/workforce/demand/recalc", json({ weekStart, locationId }), 'recalcDemandForecast'));

/* ── Operations · Services catalog (REQ-056). Source: ops-services.jsx
 * (OpsServicesScreen) — the bank's service catalog: lines of business,
 * categories, per-service channel matrix + online-bookable, usage. Dedicated
 * port (the generic events services screen doesn't carry LOB/channel shape). ── */
export interface OpsServiceLob { id: string; name: string; activated: boolean; }
export interface OpsServiceCategory { id: string; name: string; lob: string; }
export interface OpsServiceChannels { inPerson: boolean; phone: boolean; video: boolean; }
export interface OpsService {
  id: string; name: string; lob: string; category: string;
  durationMin: number; price?: number | null; onlineBookable: boolean;
  channels: OpsServiceChannels; appts30d: number; archived: boolean;
}
export interface OpsServicesResponse { lobs: OpsServiceLob[]; categories: OpsServiceCategory[]; services: OpsService[]; }

export const getOpsServices = () =>
  tag("getOpsServices", apiRaw<OpsServicesResponse>("/v1/admin/workforce/ops/services"));
export const updateOpsService = (id: string, patch: Partial<OpsService>) =>
  tag("updateOpsService", apiRaw<OpsService>(`/v1/admin/workforce/ops/services/${id}`, { ...json(patch), method: "PATCH" }, 'updateOpsService'));
export const createOpsService = (input: Partial<OpsService>) =>
  tag("createOpsService", apiRaw<OpsService>("/v1/admin/workforce/ops/services", json(input), 'createOpsService'));
export const archiveOpsService = (id: string) =>
  tag("archiveOpsService", apiRaw<{ ok: boolean }>(`/v1/admin/workforce/ops/services/${id}/archive`, { method: "POST" }, 'archiveOpsService'));
export const duplicateOpsService = (id: string) =>
  tag("duplicateOpsService", apiRaw<OpsService>(`/v1/admin/workforce/ops/services/${id}/duplicate`, { method: "POST" }, 'duplicateOpsService'));
export const setLobActivation = (lobId: string, activated: boolean) =>
  tag("setLobActivation", apiRaw<OpsServiceLob>(`/v1/admin/workforce/ops/lobs/${lobId}`, { ...json({ activated }), method: "PATCH" }, 'setLobActivation'));
export const getCoverage = (weekStart: string, locationId?: string) =>
  tag("getCoverage", apiRaw<CoverageResponse>(`/v1/admin/workforce/coverage${q({ weekStart, locationId })}`, undefined, 'getCoverage'));
export const applyCoverageSuggestion = (gapId: string, suggestionId: string) =>
  tag("applyCoverageSuggestion", apiRaw<{ gap: CoverageGap; shift?: WfShift }>(`/v1/admin/workforce/coverage/${gapId}/apply`, json({ suggestionId }), 'applyCoverageSuggestion'));

/* ── Event staffing ─────────────────────────────────────────────────── */
export const listEventStaffing = (from: string, to: string, locationId?: string) =>
  tag("listEventStaffing", apiRaw<{ items: EventStaffing[] }>(`/v1/admin/workforce/event-staffing${q({ from, to, locationId })}`, undefined, 'listEventStaffing'));
export const getEventStaffing = (eventId: string) =>
  tag("getEventStaffing", apiRaw<EventStaffing>(`/v1/admin/workforce/event-staffing/${eventId}`, undefined, 'getEventStaffing'));
export const suggestEventStaffing = (eventId: string) =>
  tag("suggestEventStaffing", apiRaw<EventStaffing>(`/v1/admin/workforce/event-staffing/${eventId}/suggest`, { method: "POST" }, 'suggestEventStaffing'));
export const updateEventStaffing = (eventId: string, roles: EventStaffingRole[]) =>
  tag("updateEventStaffing", apiRaw<EventStaffing>(`/v1/admin/workforce/event-staffing/${eventId}`, { ...json({ roles }), method: "PUT" }, 'updateEventStaffing'));
export const assignEventRole = (eventId: string, roleId: string, staffId: string) =>
  tag("assignEventRole", apiRaw<EventStaffing>(`/v1/admin/workforce/event-staffing/${eventId}/roles/${roleId}/assign`, json({ staffId }), 'assignEventRole'));

/* ── Ops board ──────────────────────────────────────────────────────── */
export const getOpsBoard = (date: string, locationId?: string) =>
  tag("getOpsBoard", apiRaw<OpsBoardSnapshot>(`/v1/admin/workforce/ops-board${q({ date, locationId })}`, undefined, 'getOpsBoard'));

/* ── Requests (manager) ─────────────────────────────────────────────── */
export const listWorkforceRequests = (status?: string) =>
  tag("listWorkforceRequests", apiRaw<{ swaps: SwapRequest[]; timeoff: TimeOffRequest[] }>(`/v1/admin/workforce/requests${q({ status })}`, undefined, 'listWorkforceRequests'));
export const decideWorkforceRequest = (id: string, decision: "approved" | "declined", note?: string) =>
  tag("decideWorkforceRequest", apiRaw<WfRequest>(`/v1/admin/workforce/requests/${id}/decision`, json({ decision, note }), 'decideWorkforceRequest'));

/* ── Labor rules ────────────────────────────────────────────────────── */
export const listLaborRules = () =>
  tag("listLaborRules", apiRaw<{ rules: LaborRule[] }>("/v1/admin/workforce/labor-rules"));
export const updateLaborRule = (id: string, patch: Partial<LaborRule>) =>
  tag("updateLaborRule", apiRaw<LaborRule>(`/v1/admin/workforce/labor-rules/${id}`, { ...json(patch), method: "PATCH" }, 'updateLaborRule'));
export const listRuleViolations = (weekStart: string, locationId?: string) =>
  tag("listRuleViolations", apiRaw<{ violations: RuleViolation[] }>(`/v1/admin/workforce/violations${q({ weekStart, locationId })}`, undefined, 'listRuleViolations'));

/* ── Self-service ───────────────────────────────────────────────────── */
export const getMySchedule = (weekStart: string) =>
  tag("getMySchedule", apiRaw<MyScheduleResponse>(`/v1/me/workforce/schedule${q({ weekStart })}`, undefined, 'getMySchedule'));
export const claimOpenShift = (shiftId: string) =>
  tag("claimOpenShift", apiRaw<WfShift>(`/v1/me/workforce/shifts/${shiftId}/claim`, { method: "POST" }, 'claimOpenShift'));
export const createSwapRequest = (input: { shiftId: string; toStaffId?: string; reason?: string; route?: "marketplace" | "direct" }) =>
  tag("createSwapRequest", apiRaw<SwapRequest>("/v1/me/workforce/requests/swap", json(input), 'createSwapRequest'));
export const createTimeOffRequest = (input: { type: TimeOffRequest["type"]; startDate: string; endDate: string; allDay: boolean; note?: string }) =>
  tag("createTimeOffRequest", apiRaw<TimeOffRequest>("/v1/me/workforce/requests/time-off", json(input), 'createTimeOffRequest'));
export const acknowledgeSchedule = () =>
  tag("acknowledgeSchedule", apiRaw<{ ok: boolean }>("/v1/me/workforce/schedule/acknowledge", { method: "POST" }, 'acknowledgeSchedule'));
export const reportSick = (input: { scope: "full-day" | "leaving-now"; backWhen: "tomorrow" | "in-2-days" | "not-sure"; note?: string }) =>
  tag("reportSick", apiRaw<{ ok: boolean; flaggedAppointments: number }>("/v1/me/workforce/report-sick", json(input), 'reportSick'));
export const getMyUpcomingShifts = () =>
  tag("getMyUpcomingShifts", apiRaw<{ shifts: { id: string; date: string; start: string; end: string; apptCount: number }[] }>("/v1/me/workforce/my-upcoming-shifts"));
export const bookAppointment = (input: { customer?: string; service?: string; sub?: string; date?: string; time?: string; durMin?: number; walkIn?: boolean; followUpFor?: string }) =>
  tag("bookAppointment", apiRaw<{ id: string }>("/v1/me/workforce/appointments", json(input), 'bookAppointment'));
export const rescheduleAppointment = (apptId: string, input: { date: string; time: string }) =>
  tag("rescheduleAppointment", apiRaw<{ ok: boolean }>(`/v1/me/workforce/appointments/${apptId}/reschedule`, json(input), 'rescheduleAppointment'));
export const getCalendarCounts = (from: string, to: string) =>
  tag("getCalendarCounts", apiRaw<{ counts: Record<string, number> }>(`/v1/me/workforce/appointments/counts${q({ from, to })}`, undefined, 'getCalendarCounts'));
export const sendAppointmentReminder = (apptId: string) =>
  tag("sendAppointmentReminder", apiRaw<{ ok: boolean }>(`/v1/me/workforce/appointments/${apptId}/reminder`, { method: "POST" }, 'sendAppointmentReminder'));
export const handWalkInsToBranch = () =>
  tag("handWalkInsToBranch", apiRaw<{ ok: boolean }>("/v1/me/workforce/walkins/hand-to-branch", { method: "POST" }, 'handWalkInsToBranch'));
export const respondToSwapRequest = (swapId: string, decision: "accept" | "decline") =>
  tag("respondToSwapRequest", apiRaw<{ ok: boolean }>(`/v1/me/workforce/swaps/${swapId}/respond`, json({ decision }), 'respondToSwapRequest'));

/* ── Sarah Chen wave · Draft review (REQ-038). Source: draft-review.jsx
 * DraftReviewScreen. ── */
export interface DraftChange { id: string; advisor: string; day: string; from: string; to: string; kind: "Time change" | "New shift" | "Removed" | "Skill block"; why: string; }
export interface ScheduleDraft {
  runId: string; weekLabel: string; generatedBy: string; generatedAt: string; reviewDue: string;
  status: "in-review" | "published" | "escalated";
  changes: DraftChange[]; advisorsCount: number; costDeltaLabel: string; coveragePct: number;
  budgetLabel: string; noticeDaysLabel: string;
}
export const getScheduleDraft = () =>
  tag("getScheduleDraft", apiRaw<ScheduleDraft>("/v1/admin/workforce/draft"));
export const finalizeScheduleDraft = (input: { action: "publish" | "escalate"; revertedChangeIds: string[] }) =>
  tag("finalizeScheduleDraft", apiRaw<ScheduleDraft>("/v1/admin/workforce/draft/finalize", json(input), 'finalizeScheduleDraft'));

/* ── Riley Khan wave · Today — live floor command center (REQ-038).
 * Source: ops-today.jsx OpsTodayScreen (read-only cross-branch pulse). ── */
export interface OpsBranchLive {
  branchId: string; branchName: string;
  apptsInProgress: number; apptsRemaining: number; walkInsWaiting: number;
  longestWaitMin: number; slaHitPct: number; occupancyPct: number; eventsToday: number;
  staffedNow: number; neededNow: number; coverageGap: number;
  alert: "queue-long" | "sla-risk" | null;
}
export interface OpsTodayResponse {
  kpis: { apptsToday: number; apptsRemaining: number; walkInsWaiting: number; avgWaitMin: number; slaHitPct: number; eventsToday: number; onlineBookings24h: number; onlineDeltaPct: number; occupancyPct: number };
  branches: OpsBranchLive[];
  events: { id: string; title: string; room: string; branchName: string; timeLabel: string; dayLabel: "Today" | "Tomorrow"; hostName: string; durationMin: number; registered: number; capacity: number }[];
  inProgress: { id: string; start: string; customer: string | null; service: string; sub?: string | null; bankerName: string; method: string; remainingMin: number }[]; // customer null when no identity fact exists (docket §2.2)
}
export const getOpsToday = () =>
  tag("getOpsToday", apiRaw<OpsTodayResponse>("/v1/admin/workforce/ops-today"));

/* ── Riley wave · Walk-ins routing + live queues (REQ-038) ── */
export interface WalkInRule {
  id: string; priority: number; trigger: string; route: string; fallback?: string | null;
  slaMin: number; status: "active" | "paused"; scheduleAware?: boolean; note?: string | null;
}
export interface WalkInRoutingResponse {
  rules: WalkInRule[];
  triggerLabels: Record<string, string>;
  routeLabels: Record<string, string>;
  /** Live per-route availability from the published schedule (free/onShift). */
  routeAvailability: Record<string, { free: number; onShift: number }>;
  stats: { slaHit24hPct: number; slaDeltaPp: number; autoRouted24h: number; autoRoutedPct: number; handoffs24h: number };
}
export interface LiveQueueEntry { name: string; service: string; serviceLabel: string; waitMin: number; member: boolean; slaMin: number }
export interface LiveQueuesResponse { asOf: string; queues: { branchId: string; branchName: string; waiting: LiveQueueEntry[] }[] }
export const getWalkInRouting = () =>
  tag("getWalkInRouting", apiRaw<WalkInRoutingResponse>("/v1/admin/workforce/walkins/routing"));
export const getLiveQueues = () =>
  tag("getLiveQueues", apiRaw<LiveQueuesResponse>("/v1/admin/workforce/walkins/queues"));
/* Docket 2026-07-18 §2.3: saveWalkInRule split into two named ops — one method per
 * call id, so the operation registry maps cleanly. */
export const createWalkInRule = (input: Omit<WalkInRule, "id">) =>
  tag("createWalkInRule", apiRaw<WalkInRule>("/v1/admin/workforce/walkins/rules", json(input), 'createWalkInRule'));
export const updateWalkInRule = (id: string, patch: Partial<Omit<WalkInRule, "id">>) =>
  tag("updateWalkInRule", apiRaw<WalkInRule>(`/v1/admin/workforce/walkins/rules/${id}`, { ...json(patch), method: "PATCH" }, 'updateWalkInRule'));
export const deleteWalkInRule = (id: string) =>
  tag("deleteWalkInRule", apiRaw<{ ok: boolean }>(`/v1/admin/workforce/walkins/rules/${id}`, { method: "DELETE" }, 'deleteWalkInRule'));
export const reorderWalkInRules = (orderedIds: string[]) =>
  tag("reorderWalkInRules", apiRaw<{ rules: WalkInRule[] }>("/v1/admin/workforce/walkins/rules/reorder", json({ orderedIds }), 'reorderWalkInRules'));

/* ── Aperture Bank wave 1 · Personal Banker (REQ-036) ── */
export const getMyDay = (date: string) =>
  tag("getMyDay", apiRaw<MyDayResponse>(`/v1/me/workforce/day${q({ date })}`, undefined, 'getMyDay'));
export const saveAppointmentOutcome = (apptId: string, input: { outcome: NonNullable<MyDayAppointment["outcome"]>; notes?: string; followUpDate?: string | null; opportunityType?: string; scheduleRepeat?: boolean; coachingFlag?: boolean; csat?: number }) =>
  tag("saveAppointmentOutcome", apiRaw<MyDayAppointment>(`/v1/me/workforce/appointments/${apptId}/outcome`, { ...json(input), method: "PATCH" }, 'saveAppointmentOutcome'));
export const acceptWalkIn = (walkInId: string, input: { mode: "now" | "after" }) =>
  tag("acceptWalkIn", apiRaw<MyDayAppointment>(`/v1/me/workforce/walkins/${walkInId}/accept`, json(input), 'acceptWalkIn'));
/* ── Service catalogue (REQ-128, 2026-08-03) ──────────────────────────
 * One document, three audiences: Riley configures, the branch manager and the advisor
 * read. The advisor's view is read-only BY CONSTRUCTION — there is no self-scoped write. */
export const getServiceCatalogue = (branch?: string | null) =>
  tag("getServiceCatalogue", apiRaw<ServiceCatalogue>(`/v1/admin/workforce/ops/catalogue${q({ branch: branch || undefined })}`, undefined, 'getServiceCatalogue'));
export const setAdvisorLobs = (advisor: string, lobs: string[]) =>
  tag("setAdvisorLobs", apiRaw<ServiceCatalogue>("/v1/admin/workforce/ops/catalogue/config", json({ advisor, lobs }), 'setAdvisorLobs'));
/** Which catalogue services a staff member is signed off to deliver. The branch manager configures
 *  this on their behalf (2026-08-03) and it reflects on that person's own My Skills screen. */
export const setAdvisorServices = (advisor: string, services: string[]) =>
  tag("setAdvisorServices", apiRaw<ServiceCatalogue>("/v1/admin/workforce/ops/catalogue/config", json({ advisor, services }), 'setAdvisorServices'));
export const setBranchLob = (branch: string, lob: string, enabled: boolean) =>
  tag("setBranchLob", apiRaw<ServiceCatalogue>("/v1/admin/workforce/ops/catalogue/config", json({ branch, lob, enabled }), 'setBranchLob'));
export const setBranchService = (branch: string, service: string, enabled: boolean) =>
  tag("setBranchService", apiRaw<ServiceCatalogue>("/v1/admin/workforce/ops/catalogue/config", json({ branch, service, enabled }), 'setBranchService'));
/* Service CRUD (2026-08-03). Seeded rows are never mutated in place: an edit is an overlay
   keyed by service id, so the operator's document stays authoritative. A seeded row can only
   be ARCHIVED; a row Riley added can be removed outright. Each write returns the whole
   catalogue so the screen never has to guess at the new state. */
export interface ServiceDraft {
  name: string; lob: string; intent: string;
  description?: string | null;
  durationMin?: number; preMin?: number; postMin?: number;
  channels?: { inPerson: boolean; phone: boolean; video: boolean };
  modules?: { appointments: boolean; queueing: boolean; events: boolean };
  questionGroups?: { appointments: string | null; queueing: string | null; events: string | null };
  disableBefore?: string | null; disableAfter?: string | null;
  minAdvance?: string | null; maxAdvance?: string | null; minCancel?: string | null;
  bookedBy?: "customer" | "advisor";
  imageUrl?: string | null; imageAlt?: string | null;
}
export const createCatalogueService = (input: ServiceDraft) =>
  tag("createCatalogueService", apiRaw<ServiceCatalogue & { created: string }>("/v1/admin/workforce/ops/catalogue/services", json(input), 'createCatalogueService'));
export const updateCatalogueService = (id: string, input: Partial<ServiceDraft> & { archived?: boolean }) =>
  tag("updateCatalogueService", apiRaw<ServiceCatalogue & { updated: string }>(`/v1/admin/workforce/ops/catalogue/services/${id}`, { ...json(input), method: "PATCH" }, 'updateCatalogueService'));
export const removeCatalogueService = (id: string) =>
  tag("removeCatalogueService", apiRaw<ServiceCatalogue & { archived?: boolean; removed?: boolean }>(`/v1/admin/workforce/ops/catalogue/services/${id}`, { method: "DELETE" }, 'removeCatalogueService'));
/* Riley extends the TREE, not just its leaves (2026-08-03). Added rows live in the same override
   store as his service edits, so the operator's seed document is never rewritten. */
export const createCatalogueLob = (input: { name: string; description?: string | null }) =>
  tag("createCatalogueLob", apiRaw<ServiceCatalogue & { created: string }>("/v1/admin/workforce/ops/catalogue/lobs", json(input), 'createCatalogueLob'));
export const createCatalogueIntent = (input: { name: string; lob: string }) =>
  tag("createCatalogueIntent", apiRaw<ServiceCatalogue & { created: string }>("/v1/admin/workforce/ops/catalogue/intents", json(input), 'createCatalogueIntent'));
/** Bulk import. Validation is per ROW server-side: good rows land, bad rows come back with the
 *  reason and their line number, and the run is recorded with who/when and the original file. */
export const importCatalogueServices = (input: {
  rows: Record<string, string>[]; by: string; byRole?: string | null;
  fileName: string; fileSize?: number; fileText?: string; uploadedAt?: string;
}) =>
  tag("importCatalogueServices", apiRaw<ServiceCatalogue & { run: CatalogueImportRun }>("/v1/admin/workforce/ops/catalogue/import", json(input), 'importCatalogueServices'));
export const getMyServiceScope = () =>
  tag("getMyServiceScope", apiRaw<MyServicesView>("/v1/me/workforce/services", undefined, 'getMyServiceScope'));

/* ── Staff-facing events + referrals (REQ-127, 2026-08-03) ──────────── */
export const listMyBookableEvents = (params: { q?: string; branch?: string; category?: string; cursor?: number; limit?: number } = {}) =>
  tag("listMyBookableEvents", apiRaw<MyEventsResponse>(`/v1/me/workforce/events${q({
    q: params.q || undefined, branch: params.branch || undefined, category: params.category || undefined,
    cursor: params.cursor ? String(params.cursor) : undefined, limit: String(params.limit ?? 4),
  })}`, undefined, 'listMyBookableEvents'));
export const registerEventGuest = (eventId: string, input: { customerName: string; customerId?: string; email?: string; mode?: "seat" | "waitlist" | "override"; note?: string }) =>
  tag("registerEventGuest", apiRaw<EventRegistrationResult>(`/v1/me/workforce/events/${eventId}/registrations`, json(input), 'registerEventGuest'));
export const createEventRegistrationLink = (eventId: string, input: { customerName?: string; customerId?: string; note?: string } = {}) =>
  tag("createEventRegistrationLink", apiRaw<EventRegistrationLink>(`/v1/me/workforce/events/${eventId}/registration-link`, json(input), 'createEventRegistrationLink'));
export const listMyReferrals = () =>
  tag("listMyReferrals", apiRaw<MyReferralsResponse>("/v1/me/workforce/referrals", undefined, 'listMyReferrals'));

export interface AvailabilityMethods { inPerson: boolean; phone: boolean; video: boolean }
export interface AvailabilityOffer { id: string; name: string; durationMin: number | null; blockMin: number; methods: AvailabilityMethods; bookable: boolean }
export interface AvailabilityBlock {
  id: string; day: string; start: string; end: string;
  /** What this window is FOR, and the colour it is drawn in everywhere. */
  name?: string | null; color?: string;
  methods: AvailabilityMethods;
  /** Empty = every service in the advisor's line of business. */
  services: string[];
  note?: string | null;
  minutes: number; allServices: boolean;
  /** The published shift this block sits inside, and the overlap that is actually bookable. */
  shiftStart?: string | null; shiftEnd?: string | null;
  effectiveStart?: string; effectiveEnd?: string;
  outsideShift?: boolean; outsideReason?: string | null;
  offers: AvailabilityOffer[]; bookableCount: number; blockedCount: number;
}
export interface MyAvailability {
  staffName: string | null; locationName: string | null;
  days: string[]; blocks: AvailabilityBlock[]; totalMinutes: number;
  palette: { id: string; name: string; hex: string }[];
  clashes: { a: string; b: string; day: string }[];
  catalogue: { id: string; name: string; lob: string; durationMin: number | null; channels: AvailabilityMethods }[];
}
/** The advisor's working pattern: which METHODS and which SERVICES each window offers.
 *  Saved as a whole list — the pattern is validated as a set (overlaps, coverage), so a partial
 *  write could leave it inconsistent. */
export const getMyAvailability = () =>
  tag("getMyAvailability", apiRaw<MyAvailability>("/v1/me/workforce/availability", undefined, 'getMyAvailability'));
export const saveMyAvailability = (blocks: Pick<AvailabilityBlock, "id" | "day" | "start" | "end" | "methods" | "services" | "note" | "name" | "color">[]) =>
  tag("saveMyAvailability", apiRaw<MyAvailability>("/v1/me/workforce/availability", { ...json({ blocks }), method: "PUT" }, 'saveMyAvailability'));

export const getMyPerformance = (range: MyPerfRange, window?: { from?: string; to?: string }) =>
  tag("getMyPerformance", apiRaw<MyPerformanceResponse>(`/v1/me/workforce/performance${q({ range, from: window?.from, to: window?.to })}`, undefined, 'getMyPerformance'));
/** Staff side of the coaching loop (REQ-126): acknowledge, mark training done, add a reflection. */
export const updateMyCoachingEntry = (entryId: string, input: { acknowledged?: boolean; trainingCompleted?: boolean; reflection?: string }) =>
  tag("updateMyCoachingEntry", apiRaw<MyCoachingEntry>(`/v1/me/workforce/coaching/${entryId}`, { ...json(input), method: "PATCH" }, 'updateMyCoachingEntry'));
export const getMyTime = () =>
  tag("getMyTime", apiRaw<MyTimeResponse>("/v1/me/workforce/time"));
export const cancelTimeOffRequest = (id: string) =>
  tag("cancelTimeOffRequest", apiRaw<{ ok: boolean }>(`/v1/me/workforce/requests/time-off/${id}`, { method: "DELETE" }, 'cancelTimeOffRequest'));
export const updateTimeOffRequest = (id: string, input: { type: TimeOffRequest["type"]; startDate: string; endDate: string; allDay: boolean; note?: string }) =>
  tag("updateTimeOffRequest", apiRaw<TimeOffRequest>(`/v1/me/workforce/requests/time-off/${id}`, { ...json(input), method: "PATCH" }, 'updateTimeOffRequest'));
export const cancelSwapRequest = (id: string) =>
  tag("cancelSwapRequest", apiRaw<{ ok: boolean }>(`/v1/me/workforce/requests/swap/${id}`, { method: "DELETE" }, 'cancelSwapRequest'));
export const updateSwapRequest = (id: string, input: { route?: "marketplace" | "direct"; reason?: string }) =>
  tag("updateSwapRequest", apiRaw<SwapRequest>(`/v1/me/workforce/requests/swap/${id}`, { ...json(input), method: "PATCH" }, 'updateSwapRequest'));

/* ── Marcus Thompson wave · Member relationships (REQ-050). Source:
 * relationships-screen.jsx (MyRelationshipsScreen) — the advisor's book of
 * members: key facts to remember for the next touchpoint + the follow-ups they
 * owe. Personal-Banker self-service surface, scoped to the signed-in advisor. ── */
export interface MemberFollowUp {
  id: string; memberId: string; member: string; initials: string;
  reason: string; channel: string; due: string; done: boolean;
}
export interface MemberBookEntry {
  id: string; name: string; initials: string; tier: string; tenure: string; products: number;
  churnRisk: "low" | "med" | "high"; riskScore?: number; preferred: string;
  lastNote: string | null; lastWhen: string | null; hasOpportunity: boolean; keyFacts: string[];
}
export interface MemberBookResponse { members: MemberBookEntry[]; followUps: MemberFollowUp[]; }

export const getMemberBook = () =>
  tag("getMemberBook", apiRaw<MemberBookResponse>("/v1/me/workforce/relationships"));
export const logContact = (memberId: string, followUpId?: string) =>
  tag("logContact", apiRaw<{ ok: boolean }>("/v1/me/workforce/relationships/contact", json({ memberId, followUpId }), 'logContact'));
export const snoozeFollowUp = (followUpId: string, days: number) =>
  tag("snoozeFollowUp", apiRaw<{ ok: boolean; due: string }>(`/v1/me/workforce/relationships/follow-ups/${followUpId}/snooze`, json({ days }), 'snoozeFollowUp'));
export const scheduleFollowUp = (memberId: string, days: number) =>
  tag("scheduleFollowUp", apiRaw<{ followUp: MemberFollowUp }>("/v1/me/workforce/relationships/follow-ups", json({ memberId, days }), 'scheduleFollowUp'));
export const addMemberFact = (memberId: string, fact: string) =>
  tag("addMemberFact", apiRaw<{ ok: boolean }>("/v1/me/workforce/relationships/facts", json({ memberId, fact }), 'addMemberFact'));
export const removeMemberFact = (memberId: string, index: number) =>
  tag("removeMemberFact", apiRaw<{ ok: boolean }>("/v1/me/workforce/relationships/facts/remove", json({ memberId, index }), 'removeMemberFact'));

/* ── My Time · Shift preferences (REQ-051). Source: draft-review.jsx
 * ShiftPreferencesEditor — advisor's persistent scheduling preferences that
 * feed the next schedule run. Lives in My Time → Preferences. ── */
export type DayPref = "prefer" | "ok" | "avoid";
export interface ShiftPreferences {
  days: Record<"Mon" | "Tue" | "Wed" | "Thu" | "Fri" | "Sat", DayPref>;
  earliest: string; latest: string; maxCloses: number; maxSaturdays: number;
  wantMoreHours: boolean; keepStable: boolean; notes: string;
  preferenceMatchPct?: number;
}
export const getShiftPreferences = () =>
  tag("getShiftPreferences", apiRaw<ShiftPreferences>("/v1/me/workforce/preferences"));
export const saveShiftPreferences = (prefs: ShiftPreferences) =>
  tag("saveShiftPreferences", apiRaw<ShiftPreferences>("/v1/me/workforce/preferences", { ...json(prefs), method: "PUT" }, 'saveShiftPreferences'));

/* ── Rules & policies · scheduling-policy surfaces (REQ-052). Source:
 * admin-rules.jsx — the three config tabs beyond Labor rules: Contracts &
 * availability, Shift templates & rotations, Optimiser objectives & fairness.
 * Admin-only; feeds every schedule-generation run. ── */
export interface PolicyContractRow {
  advisorId: string; name: string; role: string; initials: string;
  type: "Full-time" | "Part-time" | "Float"; minHrs: number; maxHrs: number;
  days: string; note: string | null; cert?: boolean;
}
export interface ShiftTemplate { id: string; name: string; time: string; breaks: string; roles: string; usedPct: string; }
export interface ShiftRotation { id: string; name: string; rule: string; state: string; }
export interface NetworkTemplate { id: string; name: string; branches: number; lobs: number; blocks: number; deployed: string; status: string; due: string; }
export interface FairnessRow { metric: string; best: string; worst: string; spread: string; trend: "Narrowing" | "Flat" | "Worsening"; }
export interface ObjectiveWeights { coverage: number; cost: number; fairness: number; prefs: number; }
export interface SchedulingPolicies {
  lastSync: string;
  kpis: { rosterCount: number; ftCount: number; ptCount: number; floatCount: number; certsExpiring: number; availabilityCoverage: string; contractedHrs: string; contractedHrsSub: string };
  contracts: PolicyContractRow[];
  templates: ShiftTemplate[];
  rotations: ShiftRotation[];
  networkTemplates: NetworkTemplate[];
  objectives: ObjectiveWeights;
  fairnessLedger: FairnessRow[];
}
export const getSchedulingPolicies = () =>
  tag("getSchedulingPolicies", apiRaw<SchedulingPolicies>("/v1/admin/workforce/policies"));
export const saveSchedulingObjectives = (weights: ObjectiveWeights) =>
  tag("saveSchedulingObjectives", apiRaw<ObjectiveWeights>("/v1/admin/workforce/policies/objectives", { ...json(weights), method: "PUT" }, 'saveSchedulingObjectives'));
export const updateSchedulingContract = (advisorId: string, patch: Partial<PolicyContractRow>) =>
  tag("updateSchedulingContract", apiRaw<PolicyContractRow>(`/v1/admin/workforce/policies/contracts/${advisorId}`, { ...json(patch), method: "PATCH" }, 'updateSchedulingContract'));
export const syncHrisContracts = () =>
  tag("syncHrisContracts", apiRaw<{ ok: boolean; lastSync: string }>("/v1/admin/workforce/policies/sync-hris", { method: "POST" }, 'syncHrisContracts'));
export const saveShiftTemplate = (input: Partial<ShiftTemplate> & { id?: string }) =>
  tag("saveShiftTemplate", apiRaw<ShiftTemplate>("/v1/admin/workforce/policies/templates", json(input), 'saveShiftTemplate'));
export const saveShiftRotation = (id: string, patch: Partial<ShiftRotation>) =>
  tag("saveShiftRotation", apiRaw<ShiftRotation>(`/v1/admin/workforce/policies/rotations/${id}`, { ...json(patch), method: "PATCH" }, 'saveShiftRotation'));
export const saveNetworkTemplate = (input: Partial<NetworkTemplate>) =>
  tag("saveNetworkTemplate", apiRaw<NetworkTemplate>("/v1/admin/workforce/policies/network-templates", json(input), 'saveNetworkTemplate'));

/* ── Requisitions · the 5 actions the prototype shipped but the master left
 * disabled (REQ-053). Source: requisitions-screen.jsx. ── */
export const addRequisitionCandidate = (reqId: string, input: { name: string; source: string; stage: ReqCandidate["stage"]; notes?: string }) =>
  tag("addRequisitionCandidate", apiRaw<Requisition>(`/v1/admin/workforce/requisitions/${reqId}/candidates`, json(input), 'addRequisitionCandidate'));
export const moveRequisitionCandidate = (reqId: string, candidateId: string, stage: ReqCandidate["stage"]) =>
  tag("moveRequisitionCandidate", apiRaw<Requisition>(`/v1/admin/workforce/requisitions/${reqId}/candidates/${candidateId}/stage`, { ...json({ stage }), method: "PATCH" }, 'moveRequisitionCandidate'));
export const closeRequisition = (reqId: string, outcome: "filled" | "cancelled", note?: string) =>
  tag("closeRequisition", apiRaw<Requisition>(`/v1/admin/workforce/requisitions/${reqId}/close`, json({ outcome, note }), 'closeRequisition'));
export const emailHiringManager = (reqId: string, message?: string) =>
  tag("emailHiringManager", apiRaw<{ ok: boolean }>(`/v1/admin/workforce/requisitions/${reqId}/email-hiring-manager`, json({ message }), 'emailHiringManager'));
export const exportRequisitions = (scope: "active" | "closed" | "all") =>
  tag("exportRequisitions", apiRaw<{ ok: boolean; filename: string; rows: number }>(`/v1/admin/workforce/requisitions/export${q({ scope })}`, undefined, 'exportRequisitions'));

/* ── Dana Russo wave (REQ-038) · Requisitions — org-wide hiring pipeline.
 * Source: requisitions-screen.jsx (admin view — Dana sees every branch). ── */
export interface ReqCandidate {
  id: string; name: string; initials: string;
  stage: "applied" | "phone-screen" | "onsite" | "reference" | "offer" | "hired" | "rejected" | "withdrew";
  source: "LinkedIn" | "Referral" | "Indeed" | "Direct"; daysInPipeline: number;
  notes?: string | null; rating?: number | null;
}
export interface Requisition {
  id: string; role: string; branch: string; locationId: string;
  hiringManager: { name: string; initials: string };
  recruiter: { name: string; initials: string } | null; // docket 2026-07-18 §2.2: null until a real identity source exists (creator-derived whoami / assign flow)
  openedAt: string; targetStart: string | null; closedAt?: string | null;
  fte: number; reason: "Backfill" | "Growth" | "Replacement";
  reasonNote?: string | null;
  priority: "standard" | "high";
  status: "sourcing" | "interviewing" | "offer-out" | "awaiting-start" | "filled" | "cancelled";
  salaryBand: string | null; description: string; // salaryBand null until tenant comp-band config exists (docket §2.2)
  candidates: ReqCandidate[];
  candidateName?: string | null; // set once filled
}
export const listRequisitions = () =>
  tag("listRequisitions", apiRaw<{ items: Requisition[] }>("/v1/admin/workforce/requisitions"));
export const updateRequisitionStatus = (reqId: string, status: Requisition["status"]) =>
  tag("updateRequisitionStatus", apiRaw<Requisition>(`/v1/admin/workforce/requisitions/${reqId}`, { ...json({ status }), method: "PATCH" }, 'updateRequisitionStatus'));

/* ── Dana Russo wave · Workforce planning (REQ-038). Source: gap-screens.jsx
 * HRPlanningScreen (planning + attrition tabs; skills tab deferred — its
 * source, SkillsRollupCard, hasn't been read yet). ── */
export interface WorkforcePlanResponse {
  fteToday: number; fteVsPlan: number; sixQuarterGap: number; openReqsPipeline: number;
  quarters: { label: string; headcount: number; plan: number; demand: number }[];
  regionGaps: { label: string; gap: number }[];
  attrition: { advisorId: string; name: string; role: string; tenure: string; branch: string; drivers: string[]; risk: number }[];
}
export const getWorkforcePlan = () =>
  tag("getWorkforcePlan", apiRaw<WorkforcePlanResponse>("/v1/admin/workforce/planning"));

/* ── Dana Russo wave · Coaching (REQ-038). Source: coaching.jsx (Insights
 * tab + staff drilldown). Grid / walk-in / distribution / loop-closure tabs
 * deferred — coaching-views.jsx / coaching-loop.jsx not yet read. ── */
export interface CoachingInsight {
  id: string; type: string; severity: number;
  banker?: string | null; bankerName?: string | null; bankerInitials?: string | null;
  title: string; detail: string; recommendation: string; cta: string;
  metric?: { current: number; target: number; unit: string } | null;
}
export interface CoachingLogEntry { kind: "1on1" | "training" | "note" | "recognition"; title: string; body: string; date: string; by: string; }
export interface CoachingStaffDetail {
  bankerId: string; name: string; role: string; apptsN: number;
  byService: { service: string; n: number; avgDur: number; branchAvgDur: number; convPct: number; branchConvPct: number }[];
  trend: { weekLabel: string; avgDur: number }[]; branchAvgDur: number;
  log: CoachingLogEntry[];
}
export const getCoachingInsights = () =>
  tag("getCoachingInsights", apiRaw<{ insights: CoachingInsight[] }>("/v1/admin/workforce/coaching/insights"));
export const getCoachingStaffDetail = (staffId: string) =>
  tag("getCoachingStaffDetail", apiRaw<CoachingStaffDetail>(`/v1/admin/workforce/coaching/staff/${staffId}`, undefined, 'getCoachingStaffDetail'));
export const addCoachingLogEntry = (bankerId: string, input: { kind: CoachingLogEntry["kind"]; title: string; body: string }) =>
  tag("addCoachingLogEntry", apiRaw<CoachingLogEntry>(`/v1/admin/workforce/coaching/staff/${bankerId}/log`, json(input), 'addCoachingLogEntry'));

/* ── Dana Russo wave · Manager Assist (REQ-038). Source: assist-analytics.jsx
 * (Overview + Audit log tabs; Settings folded into one compact panel). ── */
export interface AssistEvent { id: string; ts: string; fromName: string; fromInitials: string; reason: string; location: string; source: "auto" | "manual"; escalated: boolean; ttrSec: number | null; status: "open" | "resolved"; }
export interface AssistAnalytics {
  totalCount: number; avgTtrSec: number; medianTtrSec: number; autoPct: number; escalatedPct: number; slaPct: number;
  dailyVolume: { label: string; count: number }[];
  reasons: { label: string; count: number }[];
  bankers: { name: string; initials: string; count: number; avgTtrSec: number; escalated: number }[];
}
export interface AssistSettings { coveredBy: string | null; autoFlagOverrun: boolean; autoFlagWaitTime: boolean; escalateAfterSec: number; pushOnAll: boolean; pushOnHigh: boolean; }
export const getAssistAnalytics = (period: "today" | "7d" | "30d") =>
  tag("getAssistAnalytics", apiRaw<AssistAnalytics>(`/v1/admin/workforce/assists/analytics${q({ period })}`, undefined, 'getAssistAnalytics'));
export const listAssistEvents = (period: "today" | "7d" | "30d") =>
  tag("listAssistEvents", apiRaw<{ items: AssistEvent[] }>(`/v1/admin/workforce/assists${q({ period })}`, undefined, 'listAssistEvents'));
export const getAssistSettings = () =>
  tag("getAssistSettings", apiRaw<AssistSettings>("/v1/admin/workforce/assists/settings"));
export const updateAssistSettings = (patch: Partial<AssistSettings>) =>
  tag("updateAssistSettings", apiRaw<AssistSettings>("/v1/admin/workforce/assists/settings", { ...json(patch), method: "PATCH" }, 'updateAssistSettings'));

/* ── Dana Russo wave · Advisors (REQ-038). Source: advisors-screen.jsx
 * AdvisorsAdminView (Dana is HR, org-wide). Requisitions tab replaced by a
 * link to her own Requisitions rail item — same data, avoids a duplicate
 * screen. ── */
export interface AdvisorRow { id: string; name: string; initials: string; branch: string; role: string; status: "Active" | "Probation" | "On leave"; fte: number; adherence: number; skills: string[]; hiredLabel: string; }
export interface AdvisorRosterResponse { items: AdvisorRow[]; services: { id: string; label: string; color: string }[]; }
export const getAdvisorRoster = () =>
  tag("getAdvisorRoster", apiRaw<AdvisorRosterResponse>("/v1/admin/workforce/advisors"));

/* ═══ PORT-DUE wave (2026-07-20) — the 29 forward-declared ops (REQ-039 +
 * spec §3.5–3.14), all implemented in kit v4/v4.1+ (ratified zod contracts,
 * workforce-core executors, lifelike worlds). Types mirror the executors'
 * parsed response shapes FIELD-FOR-FIELD — verified against
 * packages/workforce-core/src/families/* this session, not guessed. ═══ */

/* §3.2 (packet 2026-07-26) — the tenant-named fallback constants are REMOVED
 * (APERTURE_REGION_ID/"region-ne", APERTURE_HERO_BRANCH_ID/"back-bay" were
 * every tenant's silent default). A workforce screen's region/branch now comes
 * ONLY from the signed-in session's scope, resolved here; when the session
 * carries none, screens surface an explicit no-scope error state — never
 * another tenant's ids, never a fabricated one.
 * Source + fallback order (also answers docket §2.2): (1) the session's
 * persona assignments via the preview demo-session bridge
 * (sessionStorage __wf_preview_session → persona.assignments, the same record
 * whoami forwards as user.assignments); (2) for branch only, the membership's
 * locations[] (screens that already hold useUser() pass it in). REQ-090 asks
 * for FIRST-CLASS regionId/branchId on the session snapshot — when it ships,
 * (1) reads that instead. Server-side the mock already CLAMPS region to the
 * caller's pinned region (live-executor applyRegionScope), so scoped callers
 * were never broadened; this removes the wrong default for everyone else. */
export function sessionScope(): { regionId: string | null; branchId: string | null } {
  try {
    const raw = sessionStorage.getItem("__wf_preview_session");
    const s = raw ? (raw.charAt(0) === "{" ? JSON.parse(raw) : { pid: raw }) : null;
    const cfg = (window as unknown as { __WF_DEMO_CFG?: { tenants: { personas: { id: string; assignments?: Record<string, string> }[] }[] } }).__WF_DEMO_CFG;
    if (s?.pid && cfg) {
      for (const t of cfg.tenants) for (const p of t.personas) if (p.id === s.pid) {
        const a = p.assignments ?? {};
        return { regionId: a.regionId ?? a.region ?? null, branchId: a.branchId ?? a.branch ?? null };
      }
    }
  } catch { /* no session — fall through to null scope */ }
  return { regionId: null, branchId: null };
}
export const NO_REGION_SCOPE_MSG = "This session has no region assignment — regional surfaces need one (REQ-090: regionId on the session snapshot). No region data can be shown.";

/* ── Dana follow-ups: Planning Skills tab · Coaching tabs · Requisition create ── */
export interface SkillsInventoryResponse {
  skills: { id: string; label: string; coveragePct: number; gapBranches: number; certified: number; expiring: number }[];
  byBranch: { branchId: string; name: string; skills: { skillId: string; n: number }[] }[];
}
export const getSkillsInventory = () =>
  tag("getSkillsInventory", apiRaw<SkillsInventoryResponse>("/v1/admin/workforce/planning/skills"));

export interface CoachingLoopItem {
  insightId: string; insightTitle: string;
  action: { kind: string; title: string; date: string; by: string };
  status: "open" | "actioned" | "resolved" | "regressed";
  metricDelta: { label: string; before: number; after: number; unit: string };
}
export const getCoachingLoop = () =>
  tag("getCoachingLoop", apiRaw<{ items: CoachingLoopItem[] }>("/v1/admin/workforce/coaching/loop"));
export interface CoachingGridRow { bankerId: string; name: string; branch: string; convPct: number; avgDur: number; csat: number; apptsN: number; quadrant: "star" | "develop" | "coach" | "watch"; }
export const getCoachingGrid = () =>
  tag("getCoachingGrid", apiRaw<{ rows: CoachingGridRow[] }>("/v1/admin/workforce/coaching/grid"));
export interface CoachingPatternsResponse {
  walkIns: { day: string; hour: number; count: number }[];
  distribution: { bankerId: string; name: string; apptShare: number; walkInShare: number; fairnessIdx: number }[];
}
export const getCoachingPatterns = () =>
  tag("getCoachingPatterns", apiRaw<CoachingPatternsResponse>("/v1/admin/workforce/coaching/patterns"));

/** Canonical enum standard|high both sides (docket 2026-07-18 §2.3). */
export const createRequisition = (input: { role: string; branchId: string; priority: "standard" | "high"; hiringManager: string; note?: string }) =>
  tag("createRequisition", apiRaw<Requisition>("/v1/admin/workforce/requisitions", json(input), 'createRequisition'));

/* ── Advisors — per-advisor profile (spec §3.9) ── */
export interface AdvisorProfileResponse {
  advisor: AdvisorRow;
  perf: { convPct: number; avgDur: number; csat: number; apptsN: number; trend: { weekLabel: string; value: number }[] };
  schedule: { day: string; shifts: { id: string; date: string; start: string; end: string; role: string }[] }[];
  coachingLog: CoachingLogEntry[];
  skillsDetail: { skill: string; certifiedAt?: string; expires?: string }[];
}
export const getAdvisorProfile = (staffId: string) =>
  tag("getAdvisorProfile", apiRaw<AdvisorProfileResponse>(`/v1/admin/workforce/advisors/${staffId}`, undefined, 'getAdvisorProfile'));

/* ── Compliance posture — Riley + Chris (spec §3.13) ── */
export interface ComplianceJurisdiction {
  code: string; label: string;
  rules: { id: string; label: string; kind: string; status: "compliant" | "at-risk" | "violation"; detail: string }[];
}
export interface CompliancePostureResponse { jurisdictions: ComplianceJurisdiction[]; violations: RuleViolation[]; lastReviewed: string; }
export const getCompliancePosture = (scope?: { regionId?: string; locationId?: string }) =>
  tag("getCompliancePosture", apiRaw<CompliancePostureResponse>(`/v1/admin/workforce/compliance/posture${q({ regionId: scope?.regionId, locationId: scope?.locationId })}`, undefined, 'getCompliancePosture'));

/* ── Priya — regional suite (spec §3.10; regionId clamped by the principal) ── */
export interface RegionBranchRow { id: string; name: string; manager: string; adherencePct: number; coveragePct: number; avgWaitMin: number; conversionPct: number; alerts: number; status: "healthy" | "watch" | "critical"; }
export interface RegionOverviewResponse {
  region: { id: string; label: string; branchCount: number };
  kpis: { adherencePct: number; coveragePct: number; avgWaitMin: number; conversionPct: number; openGaps: number };
  branches: RegionBranchRow[];
  attention: { id: string; branchId: string; title: string; detail: string; severity: number }[];
}
export const getRegionOverview = (regionId: string) =>
  tag("getRegionOverview", apiRaw<RegionOverviewResponse>(`/v1/admin/workforce/region/${regionId}/overview`, undefined, 'getRegionOverview'));
export interface RegionCompareRow { branchId: string; name: string; values: { conversion: number; nps: number; wait: number; adherence: number; revPerFte: number }; rank: number; trend: "up" | "flat" | "down"; }
export const getRegionCompare = (regionId: string) =>
  tag("getRegionCompare", apiRaw<{ metrics: string[]; rows: RegionCompareRow[] }>(`/v1/admin/workforce/region/${regionId}/compare`, undefined, 'getRegionCompare'));
export interface SecondmentItem { id: string; advisorId: string; advisorName: string; fromBranch: string; toBranch: string; from: string; to: string; status: "proposed" | "active" | "completed" | "declined"; reason: string; }
export interface SecondmentsResponse {
  items: SecondmentItem[];
  marketplaceRadiusMi: number;
  splitCoverage: { advisorId: string; advisorName: string; branches: { branchId: string; days: string[] }[] }[];
}
export const listSecondments = (regionId: string) =>
  tag("listSecondments", apiRaw<SecondmentsResponse>(`/v1/admin/workforce/region/${regionId}/secondments`, undefined, 'listSecondments'));
export const proposeSecondment = (regionId: string, input: { advisorId: string; toBranch: string; from: string; to: string; reason: string }) =>
  tag("proposeSecondment", apiRaw<SecondmentItem>(`/v1/admin/workforce/region/${regionId}/secondments`, json(input), 'proposeSecondment'));
export interface GenerateRunRow { runId: string; branchId: string; branchName: string; week: string; status: "published" | "in-review" | "escalated" | "failed"; generatedAt: string; generatedBy: string; coveragePct: number; costDelta: number; changeCount: number; }
export const getRegionRuns = (regionId: string) =>
  tag("getRegionRuns", apiRaw<{ runs: GenerateRunRow[] }>(`/v1/admin/workforce/region/${regionId}/runs`, undefined, 'getRegionRuns'));
export interface RegionScopeResponse {
  readBranches: string[]; writeBranches: string[];
  approvals: { kind: string; allowed: boolean }[];
  members: { id: string; name: string; role: string }[];
}
export const getRegionScope = (regionId: string) =>
  tag("getRegionScope", apiRaw<RegionScopeResponse>(`/v1/admin/workforce/region/${regionId}/scope`, undefined, 'getRegionScope'));

/* ── Sarah — Floor board (spec §3.11) ── */
export interface FloorAlert { id: string; kind: "gap" | "overrun" | "walkin-surge" | "sla"; severity: 1 | 2 | 3; title: string; detail: string; sinceMin: number; suggestedAction: string; }
export interface FloorBoardResponse {
  asOf: string;
  coverage: { zone: string; needed: number; present: number }[];
  alerts: FloorAlert[];
  assistFeed: AssistEvent[];
  reflow: { id: string; advisorName: string; from: string; to: string; action: string }[];
}
export const getFloorBoard = (locationId: string) =>
  tag("getFloorBoard", apiRaw<FloorBoardResponse>(`/v1/admin/workforce/floor/${locationId}`, undefined, 'getFloorBoard'));
export const resolveFloorAlert = (alertId: string, input: { action: "accept-suggestion" | "dismiss" | "assign"; assigneeId?: string }) =>
  tag("resolveFloorAlert", apiRaw<{ alert: FloorAlert; applied: boolean }>(`/v1/admin/workforce/floor/alerts/${alertId}/resolve`, json(input), 'resolveFloorAlert'));

/* ── Chris — admin suite A: setup · copilot · runs · reporting (spec §3.12) ── */
export interface SetupStep { id: string; label: string; status: "done" | "in-progress" | "todo"; detail: string; href: string; }
export interface SetupProgressResponse { steps: SetupStep[]; pctComplete: number; blocking: string[]; }
export const getSetupProgress = () =>
  tag("getSetupProgress", apiRaw<SetupProgressResponse>("/v1/admin/workforce/setup"));
export const completeSetupStep = (step: string) =>
  tag("completeSetupStep", apiRaw<SetupProgressResponse>(`/v1/admin/workforce/setup/${step}/complete`, { method: "POST" }, 'completeSetupStep'));
export interface CopilotRunResult {
  planId: string; summary: string; changes: DraftChange[];
  metrics: { coverageDeltaPct: number; costDelta: number; violations: number };
  /** Additive honesty label on the provisional contract (executor cycles kind/day positionally). */
  provisional?: string;
}
export const runCopilot = (input: { goal: string; weekStart: string; locationId?: string }) =>
  tag("runCopilot", apiRaw<CopilotRunResult>("/v1/admin/workforce/copilot/run", json(input), 'runCopilot'));
export const applyCopilotPlan = (planId: string) =>
  tag("applyCopilotPlan", apiRaw<ScheduleDraft>(`/v1/admin/workforce/copilot/${planId}/apply`, { method: "POST" }, 'applyCopilotPlan'));
export const listGenerateRuns = (week?: string) =>
  tag("listGenerateRuns", apiRaw<{ runs: GenerateRunRow[] }>(`/v1/admin/workforce/runs${q({ week })}`, undefined, 'listGenerateRuns'));
export type RunDetailResponse = GenerateRunRow & { changes: DraftChange[]; audit: { ts: string; actor: string; action: string }[]; validation: { errors: number; warnings: number } };
export const getRunDetail = (runId: string) =>
  tag("getRunDetail", apiRaw<RunDetailResponse>(`/v1/admin/workforce/runs/${runId}`, undefined, 'getRunDetail'));
export interface NetworkReportRow { branch: string; plannedHours: number; actualHours: number; variancePct: number; overtimePct: number; adherencePct: number; }
export interface ReportBoard { id: string; name: string; cadence: string; recipients: string[]; lastRun: string; }
export const getWorkforceReport = (kind: "network" | "variance" | "boards", range: string) =>
  tag("getWorkforceReport", apiRaw<{ rows: NetworkReportRow[]; boards: ReportBoard[] }>(`/v1/admin/workforce/reports${q({ kind, range })}`, undefined, 'getWorkforceReport'));

/* ── Chris — admin suite B: payroll · marketplace · integration (spec §3.12) ── */
export interface PayrollCycle { id: string; label: string; from: string; to: string; status: "open" | "locked" | "exported" | "reconciled"; exceptionCount: number; totalHours: number; totalCost: number; }
export interface PayrollException { staffId: string; name: string; kind: "missing-punch" | "overtime-unapproved" | "rate-mismatch"; detail: string; }
export const getPayrollCycles = () =>
  tag("getPayrollCycles", apiRaw<{ cycles: PayrollCycle[] }>("/v1/admin/workforce/payroll/cycles"));
export interface PayrollConnector { id: string; vendor: "UKG" | "ADP" | "SAP"; status: "connected" | "error" | "not-configured"; lastSync: string | null; mappingSummary: string; }
export const listPayrollConnectors = () =>
  tag("listPayrollConnectors", apiRaw<{ connectors: PayrollConnector[] }>("/v1/admin/workforce/payroll/connectors"));
export const reconcilePayrollCycle = (cycleId: string) =>
  tag("reconcilePayrollCycle", apiRaw<{ cycle: PayrollCycle; exceptions: PayrollException[] }>(`/v1/admin/workforce/payroll/cycles/${cycleId}/reconcile`, { method: "POST" }, 'reconcilePayrollCycle'));
export interface MarketplaceShift {
  id: string; branch: string; role: string; date: string; start: string; end: string;
  premiumPct: number; postedBy: string; reason: string; status: "open" | "claimed" | "expired";
  claims: { staffId: string; name: string; at: string }[];
  locationId?: string; regionId?: string; filledAfterHrs?: number | null;
}
export const listMarketplaceShifts = (scope?: { regionId?: string; locationId?: string }) =>
  tag("listMarketplaceShifts", apiRaw<{ shifts: MarketplaceShift[]; fillRatePct: number; avgTimeToFillHrs: number }>(`/v1/admin/workforce/marketplace/shifts${q({ regionId: scope?.regionId, locationId: scope?.locationId })}`, undefined, 'listMarketplaceShifts'));
export interface MarketplacePolicy { radiusMi: number; crossBranch: boolean; autoApprove: boolean; premiumCapPct: number; eligibility: string[]; }
export const getMarketplacePolicy = () =>
  tag("getMarketplacePolicy", apiRaw<MarketplacePolicy>("/v1/admin/workforce/marketplace/policy"));
export const updateMarketplacePolicy = (patch: Partial<MarketplacePolicy>) =>
  tag("updateMarketplacePolicy", apiRaw<MarketplacePolicy>("/v1/admin/workforce/marketplace/policy", { ...json(patch), method: "PATCH" }, 'updateMarketplacePolicy'));
export interface WfIntegrationStatus {
  link: { status: "healthy" | "degraded" | "down"; lastSync: string; direction: "bidirectional" };
  contracts: { name: string; version: string; state: "active" | "draft" }[];
  recentEvents: { ts: string; kind: string; detail: string }[];
}
export const getWfIntegrationStatus = () =>
  tag("getWfIntegrationStatus", apiRaw<WfIntegrationStatus>("/v1/admin/workforce/integration"));
